import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  addProjectEffort,
  applyParticipationGrowth,
  clampToPlot,
  clampToInterior,
  createDemoBootstrap,
  defaultInteriorPosition,
  defaultPlacementPosition,
  evaluateExteriorPlacement,
  evaluateHomeChoice,
  evaluateInteriorPlacement,
  evaluatePlotExpansion,
  getCatalogItem,
  getFootprint,
  getHomeType,
  interiorCapacity,
  largestFittingScaleStep,
  placementBounds,
  PLACEMENT_SCALE_MAX_STEP,
  STARTER_HOME_ITEM_IDS,
  supportHeightAt,
  type ActivityBootstrap,
  type HomeType,
  type ItemPlacement,
  type PetSpecies,
  type PropertyState,
  validateQuestCopy
} from "@tribe/domain";
import { apiMutation } from "./api";

export type PanelId = "village" | "home" | "quests" | "shop" | "collection" | "gifts" | "project" | "admin" | null;
export type Quality = "low" | "balanced" | "high";

type Toast = { id: string; message: string; tone: "success" | "error" | "info" };
export type LiveVisitor = { memberId: string; displayName: string; x: number; z: number; reaction?: string; disconnectedAt?: number };

export type InteriorPhase = "closed" | "entering" | "open" | "exiting";

interface VillageStore {
  data: ActivityBootstrap;
  selectedPlotId: string | null;
  cameraTarget: { x: number; z: number };
  interiorPlotId: string | null;
  interiorPhase: InteriorPhase;
  panel: PanelId;
  quality: Quality;
  reducedMotion: boolean;
  /**
   * How loud the village is, from 0 to 1. Zero is a real off switch: the ambience system releases
   * the audio device and downloads nothing while it is muted.
   */
  ambienceVolume: number;
  questDraft: string;
  /** The channel and verification the draft is written for. Sent with it, rather than assumed. */
  questChannelGroup: string;
  questVerification: string;
  toast: Toast | null;
  liveVisitors: LiveVisitor[];
  /** Which yard item the editor is working on. Never persisted — it is a view concern. */
  selectedPlacementId: string | null;
  setBootstrap: (data: ActivityBootstrap) => void;
  setPanel: (panel: PanelId) => void;
  selectPlot: (plotId: string | null) => void;
  enterInterior: (plotId: string) => void;
  exitInterior: () => void;
  setInteriorPhase: (phase: InteriorPhase) => void;
  setQuality: (quality: Quality) => void;
  setReducedMotion: (reduced: boolean) => void;
  setAmbienceVolume: (volume: number) => void;
  setQuestDraft: (copy: string) => void;
  setQuestChannelGroup: (group: string) => void;
  setQuestVerification: (verification: string) => void;
  dismissToast: () => void;
  completeQuest: (questId: string) => void;
  purchase: (itemId: string) => void;
  adoptPet: (species: PetSpecies, name: string, appearance: string) => void;
  renamePet: (petId: string, name: string) => void;
  plant: (itemId: string) => void;
  chooseHome: (type: HomeType) => void;
  expandPlot: () => void;
  selectPlacement: (placementId: string | null) => void;
  movePlacement: (placementId: string, position: { x: number; z: number }) => void;
  nudgePlacement: (placementId: string, dx: number, dz: number) => void;
  rotatePlacement: (placementId: string, quarterTurns: number) => void;
  setPlacementScale: (placementId: string, scaleStep: number) => void;
  removePlacement: (placementId: string) => void;
  togglePlacement: (itemId: string) => void;
  toggleInteriorPlacement: (itemId: string) => void;
  donateToProject: (copper: number) => void;
  sendGift: (itemId: string, recipientId: string) => void;
  saveQuestDraft: () => void;
  /** For the parts of the interface that talk to the Worker directly rather than through an action. */
  showToast: (message: string, tone: Toast["tone"]) => void;
  receivePresence: (message: unknown) => void;
}

const show = (message: string, tone: Toast["tone"]): Toast => ({ id: crypto.randomUUID(), message, tone });

/**
 * Saving the property, at most once per breath.
 *
 * Rotating an item is three clicks in two seconds and dragging one is a hundred frames. Each of
 * those is a legitimate change, but none of them is worth its own round trip, so edits settle for
 * a moment before the write goes out. The version sent is the one the server last acknowledged —
 * its optimistic-concurrency check compares against that, not against our local increment.
 */
function createPropertySaver(getBootstrap: () => ActivityBootstrap, onError: (message: string) => void) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let baseVersion: number | undefined;

  return (versionBeforeEdit: number) => {
    baseVersion ??= versionBeforeEdit;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      const property = { ...getBootstrap().property, version: baseVersion! };
      baseVersion = undefined;
      timer = undefined;
      void apiMutation("/api/v1/property", { property }, "PATCH").catch((error: unknown) => {
        onError(error instanceof Error ? error.message : "That change could not be saved");
      });
    }, 400);
  };
}

/**
 * Applies a change to one placement, but only if the result is somewhere it is allowed to be.
 *
 * Every edit runs through the same domain rule the server will run, so an optimistic update can
 * never paint an item somewhere the save would reject. A refused edit leaves the world untouched
 * and explains itself.
 */
function editPlacement(
  set: (partial: Partial<VillageStore>) => void,
  get: () => VillageStore,
  placementId: string,
  change: (placement: ItemPlacement, property: PropertyState) => ItemPlacement
): void {
  const state = get();
  const property = state.data.property;
  const current = property.placements.find((entry) => entry.id === placementId);
  if (!current) return;

  const next = change(current, property);
  const item = getCatalogItem(next.itemId);
  const check = next.surface === "interior" && property.homeType
    ? evaluateInteriorPlacement({
        itemId: next.itemId,
        itemName: item.name,
        position: next.position,
        rotationQuarters: next.rotationQuarters,
        scaleStep: next.scaleStep,
        homeType: property.homeType,
        others: indoorsIn(property.placements),
        ignoreId: next.id
      })
    : evaluateExteriorPlacement({
        itemId: next.itemId,
        itemName: item.name,
        position: next.position,
        rotationQuarters: next.rotationQuarters,
        scaleStep: next.scaleStep,
        plotTier: property.plotTier,
        homeType: property.homeType
      });
  if (!check.ok) {
    set({ toast: show(check.message, "error") });
    return;
  }

  const edited = property.placements.map((entry) => (entry.id === placementId ? next : entry));
  set({
    data: {
      ...state.data,
      property: {
        ...property,
        placements: settleInterior(edited, next.surface === "interior"),
        version: property.version + 1
      }
    }
  });
  saveProperty(property.version);
}

/** Everything indoors, which is the set any interior rule is measured against. */
function indoorsIn(placements: ItemPlacement[]): ItemPlacement[] {
  return placements.filter((entry) => entry.surface === "interior");
}

/**
 * Drops anything left hovering back onto whatever is now underneath it.
 *
 * Stacking makes placements depend on each other: take the table away and the plant that was on it
 * is suddenly floating, and the save the server sees would be a plant hanging in mid-air. Rather
 * than forbid that arrangement — which would refuse the move outright and strand the member — the
 * home re-settles after every edit. Lowest first, so a stack of three comes down in one pass.
 */
function settleInterior(placements: ItemPlacement[], touched: boolean): ItemPlacement[] {
  if (!touched) return placements;
  const indoors = indoorsIn(placements).slice().sort((a, b) => a.position.y - b.position.y);
  const settled = new Map<string, ItemPlacement>();

  for (const placement of indoors) {
    const bounds = placementBounds(
      getFootprint(placement.itemId), placement.rotationQuarters, placement.scaleStep
    );
    const below = indoors
      .map((entry) => settled.get(entry.id) ?? entry)
      .filter((entry) => entry.id !== placement.id && entry.position.y < placement.position.y - 1e-6);
    const restingOn = supportHeightAt({
      position: placement.position,
      bounds,
      placements: below,
      ignoreId: placement.id
    });
    settled.set(
      placement.id,
      restingOn === placement.position.y
        ? placement
        : { ...placement, position: { ...placement.position, y: restingOn } }
    );
  }

  return placements.map((placement) => settled.get(placement.id) ?? placement);
}

/** Assigned once the store exists, because the saver needs to read from it. */
let saveProperty: (versionBeforeEdit: number) => void = () => {};

export const useVillageStore = create<VillageStore>()(
  persist(
    (set, get) => ({
      data: createDemoBootstrap(),
      selectedPlotId: null,
      cameraTarget: { x: 0, z: 0 },
      interiorPlotId: null,
      interiorPhase: "closed",
      panel: null,
      quality: "balanced",
      reducedMotion: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
      // Present but quiet. A Discord Activity that opens at full volume over a voice call is the
      // reason people reach for the mute button before they reach for anything else.
      ambienceVolume: 0.35,
      questDraft: "What game always turns into a good story with friends?",
      questChannelGroup: "general",
      questVerification: "message",
      toast: null,
      liveVisitors: [],
      selectedPlacementId: null,
      setBootstrap: (data) => set({ data, ...(data.rewardInbox.length ? { toast: show(`While you were away, ${data.rewardInbox.reduce((sum, reward) => sum + reward.copper, 0)} copper reached your wallet`, "success") } : {}) }),
      setPanel: (panel) => set({ panel }),
      selectPlot: (selectedPlotId) => {
        // Selecting somewhere else is an implicit request to leave the house you are in.
        if (get().interiorPlotId && get().interiorPlotId !== selectedPlotId) get().exitInterior();
        const plot = get().data.world.plots.find((candidate) => candidate.id === selectedPlotId);
        set({ selectedPlotId, panel: selectedPlotId ? "village" : get().panel, ...(plot ? { cameraTarget: { x: plot.position.x, z: plot.position.z } } : {}) });
      },
      enterInterior: (plotId) => {
        const plot = get().data.world.plots.find((candidate) => candidate.id === plotId);
        if (!plot) return;
        set({
          interiorPlotId: plotId,
          interiorPhase: "entering",
          selectedPlotId: plotId,
          cameraTarget: { x: plot.position.x, z: plot.position.z },
          // The home panel, because indoors it becomes the room editor — the things you would want
          // the moment you step in. It carries its own way back out, so the village panel is no
          // longer needed just to host the exit.
          panel: "home"
        });
      },
      exitInterior: () => {
        if (get().interiorPhase === "closed") return;
        set({ interiorPhase: "exiting" });
      },
      setInteriorPhase: (interiorPhase) =>
        set(interiorPhase === "closed" ? { interiorPhase, interiorPlotId: null } : { interiorPhase }),
      setQuality: (quality) => set({ quality }),
      setReducedMotion: (reducedMotion) => set({ reducedMotion }),
      setAmbienceVolume: (volume) => set({ ambienceVolume: Math.min(1, Math.max(0, volume)) }),
      setQuestDraft: (questDraft) => set({ questDraft }),
      setQuestChannelGroup: (questChannelGroup) => set({ questChannelGroup }),
      setQuestVerification: (questVerification) => set({ questVerification }),
      dismissToast: () => set({ toast: null }),
      completeQuest: (questId) => {
        set((state) => {
        const quest = state.data.activeQuests.find((candidate) => candidate.id === questId);
        if (!quest) return { toast: show("That quest is no longer active", "error") };
        // The first quest is what unlocks the home types and opens the living room. Mirrors the
        // worker so the preview build and the real one reach the same state.
        const firstHome = !STARTER_HOME_ITEM_IDS.every((itemId) => state.data.ownedItemIds.includes(itemId));
        // Shown straight away so the garden reacts to the quest, but the Worker is what decides
        // it — a property save is explicitly not allowed to move growth or affection.
        const property = {
          ...applyParticipationGrowth(state.data.property),
          unlockedRooms: firstHome ? ["living" as const] : state.data.property.unlockedRooms,
          version: state.data.property.version + 1
        };
        return {
          data: {
            ...state.data,
            wallet: { copper: state.data.wallet.copper + quest.rewardCopper },
            project: addProjectEffort(state.data.project, quest.projectEffort),
            ownedItemIds: firstHome ? [...new Set([...state.data.ownedItemIds, ...STARTER_HOME_ITEM_IDS])] : state.data.ownedItemIds,
            property
          },
          panel: firstHome ? "home" : state.panel,
          toast: show(
            firstHome
              ? "Quest complete. Your parcel is ready, so pick the home you want on it."
              : `Quest complete. You earned ${quest.rewardCopper} copper.`,
            "success"
          )
        };
        });
        void apiMutation("/api/v1/quests/complete", { questId, sourceMessageId: `activity-preview-${crypto.randomUUID()}`, verification: { method: "activity_link" } })
          .catch((error: unknown) => set({ toast: show(error instanceof Error ? error.message : "The quest reward could not be saved", "error") }));
      },
      purchase: (itemId) => {
        set((state) => {
        const catalogItem = getCatalogItem(itemId);
        if (catalogItem.achievement) return { toast: show("That item is earned through an achievement", "error") };
        if (state.data.ownedItemIds.includes(itemId) && !catalogItem.stackable) return { toast: show("You already own that item", "info") };
        if (state.data.wallet.copper < catalogItem.priceCopper) return { toast: show("You do not have enough coins yet", "error") };
        return {
          data: {
            ...state.data,
            wallet: { copper: state.data.wallet.copper - catalogItem.priceCopper },
            ownedItemIds: [...state.data.ownedItemIds, itemId]
          },
          toast: show(`${catalogItem.name} is now in your collection`, "success")
        };
        });
        void apiMutation("/api/v1/purchases", { itemId }).catch((error: unknown) => set({ toast: show(error instanceof Error ? error.message : "That purchase could not be saved", "error") }));
      },
      adoptPet: (species, name, appearance) => {
        const originalVersion = get().data.property.version;
        set((state) => {
        if (state.data.property.pets.length >= 3) return { toast: show("Your home already has three pets", "error") };
        const trimmed = name.trim();
        if (trimmed.length < 1 || trimmed.length > 24) return { toast: show("Pet names need 1 to 24 characters", "error") };
        const itemId = `pet-${species}`;
        const catalogItem = getCatalogItem(itemId);
        if (state.data.wallet.copper < catalogItem.priceCopper) return { toast: show("You need 5 silver to adopt this pet", "error") };
        return {
          data: {
            ...state.data,
            wallet: { copper: state.data.wallet.copper - catalogItem.priceCopper },
            ownedItemIds: [...new Set([...state.data.ownedItemIds, itemId])],
            property: {
              ...state.data.property,
              pets: [...state.data.property.pets, { id: crypto.randomUUID(), species, name: trimmed, appearance, affection: 0, slot: state.data.property.pets.length, unlockedAnimations: ["look"] }],
              version: state.data.property.version + 1
            }
          },
          toast: show(`${trimmed} has moved in`, "success")
        };
        });
        const property = { ...get().data.property, version: originalVersion };
        void apiMutation("/api/v1/property", { property }, "PATCH").catch((error: unknown) => set({ toast: show(error instanceof Error ? error.message : "That pet could not be saved", "error") }));
      },
      renamePet: (petId, name) => set((state) => {
        const trimmed = name.trim();
        if (trimmed.length < 1 || trimmed.length > 24) return { toast: show("Pet names need 1 to 24 characters", "error") };
        return {
          data: { ...state.data, property: { ...state.data.property, pets: state.data.property.pets.map((pet) => pet.id === petId ? { ...pet, name: trimmed } : pet), version: state.data.property.version + 1 } },
          toast: show("Pet name saved", "success")
        };
      }),
      plant: (itemId) => {
        const originalVersion = get().data.property.version;
        set((state) => {
        if (state.data.property.plants.length >= 4) return { toast: show("All four garden beds are full", "error") };
        const catalogItem = getCatalogItem(itemId);
        if (!state.data.ownedItemIds.includes(itemId)) {
          if (state.data.wallet.copper < catalogItem.priceCopper) return { toast: show("You do not have enough coins for those seeds", "error") };
        }
        return {
          data: {
            ...state.data,
            wallet: state.data.ownedItemIds.includes(itemId) ? state.data.wallet : { copper: state.data.wallet.copper - catalogItem.priceCopper },
            ownedItemIds: [...new Set([...state.data.ownedItemIds, itemId])],
            property: {
              ...state.data.property,
              plants: [...state.data.property.plants, { id: crypto.randomUUID(), itemId, bedIndex: state.data.property.plants.length, growth: 0, plantedAt: new Date().toISOString() }],
              version: state.data.property.version + 1
            }
          },
          toast: show(`${catalogItem.name} is planted`, "success")
        };
        });
        const property = { ...get().data.property, version: originalVersion };
        void apiMutation("/api/v1/property", { property }, "PATCH").catch((error: unknown) => set({ toast: show(error instanceof Error ? error.message : "That planting could not be saved", "error") }));
      },
      chooseHome: (type) => {
        let accepted = false;
        set((state) => {
          const decision = evaluateHomeChoice({
            property: state.data.property,
            ownedItemIds: state.data.ownedItemIds,
            walletCopper: state.data.wallet.copper,
            type
          });
          if (!decision.ok) return { toast: show(decision.message, "error") };
          accepted = true;
          const home = getHomeType(type);
          return {
            data: {
              ...state.data,
              wallet: { copper: state.data.wallet.copper - decision.costCopper },
              property: { ...state.data.property, homeType: type, version: state.data.property.version + 1 }
            },
            toast: show(
              decision.isFirstHome
                ? `The ${home.name} is yours. Welcome home.`
                : `You moved into the ${home.name}.`,
              "success"
            )
          };
        });
        if (!accepted) return;
        void apiMutation("/api/v1/home", { homeType: type })
          .catch((error: unknown) => set({ toast: show(error instanceof Error ? error.message : "That home change could not be saved", "error") }));
      },
      /**
       * Picking an item up is what puts the yard into edit mode.
       *
       * The panel gets out of the way while it lasts. Arranging a garden means looking at the
       * garden, and on a phone the panel is anchored across the bottom of it — the controls were
       * sitting on top of the thing they were meant to be adjusting.
       */
      selectPlacement: (selectedPlacementId) =>
        set((state) => ({
          selectedPlacementId,
          panel: selectedPlacementId ? null : state.panel ?? "home"
        })),

      movePlacement: (placementId, position) => {
        editPlacement(set, get, placementId, (placement, property) => {
          const bounds = placementBounds(
            getFootprint(placement.itemId), placement.rotationQuarters, placement.scaleStep
          );
          // Clamped rather than refused: a drag that runs past the fence should stop at it, not
          // throw the item back to where it started.
          if (placement.surface === "interior" && property.homeType) {
            const clamped = clampToInterior({ position, bounds, homeType: property.homeType });
            // Where a thing lands is decided by what is underneath it — the floor, or the top of
            // the table it was dragged over. Height is never something the member sets directly.
            const y = supportHeightAt({
              position: clamped,
              bounds,
              placements: indoorsIn(property.placements),
              ignoreId: placement.id
            });
            return { ...placement, position: { x: clamped.x, y, z: clamped.z } };
          }
          const clamped = clampToPlot({ position, bounds, plotTier: property.plotTier });
          return { ...placement, position: { ...placement.position, x: clamped.x, z: clamped.z } };
        });
      },

      nudgePlacement: (placementId, dx, dz) => {
        const placement = get().data.property.placements.find((entry) => entry.id === placementId);
        if (!placement) return;
        get().movePlacement(placementId, { x: placement.position.x + dx, z: placement.position.z + dz });
      },

      rotatePlacement: (placementId, quarterTurns) => {
        editPlacement(set, get, placementId, (placement) => ({
          ...placement,
          rotationQuarters: (((placement.rotationQuarters + quarterTurns) % 4) + 4) % 4
        }));
      },

      setPlacementScale: (placementId, scaleStep) => {
        editPlacement(set, get, placementId, (placement) => ({
          ...placement,
          scaleStep: Math.min(PLACEMENT_SCALE_MAX_STEP, Math.max(0, Math.round(scaleStep)))
        }));
      },

      removePlacement: (placementId) => {
        const versionBefore = get().data.property.version;
        let removed = false;
        set((state) => {
          const placement = state.data.property.placements.find((entry) => entry.id === placementId);
          if (!placement) return {};
          removed = true;
          const item = getCatalogItem(placement.itemId);
          const remaining = state.data.property.placements.filter((entry) => entry.id !== placementId);
          return {
            selectedPlacementId: state.selectedPlacementId === placementId ? null : state.selectedPlacementId,
            data: {
              ...state.data,
              property: {
                ...state.data.property,
                // Taking a table away lets whatever stood on it down onto the floor.
                placements: settleInterior(remaining, placement.surface === "interior"),
                version: state.data.property.version + 1
              }
            },
            toast: show(`${item.name} moved to your collection`, "success")
          };
        });
        if (removed) saveProperty(versionBefore);
      },

      expandPlot: () => {
        let accepted = false;
        set((state) => {
          const decision = evaluatePlotExpansion({ property: state.data.property, wallet: state.data.wallet });
          if (!decision.ok) return { toast: show(decision.message, "error") };
          accepted = true;
          return {
            data: {
              ...state.data,
              wallet: { copper: state.data.wallet.copper - decision.costCopper },
              property: { ...state.data.property, plotTier: decision.nextTier, version: state.data.property.version + 1 }
            },
            toast: show("Your parcel now reaches further. There is room for more out there.", "success")
          };
        });
        if (!accepted) return;
        void apiMutation("/api/v1/plot/expand", {})
          .catch((error: unknown) => set({ toast: show(error instanceof Error ? error.message : "That expansion could not be saved", "error") }));
      },
      /**
       * Puts a piece of furniture into the room the member is standing in, or takes it back out.
       *
       * The indoor twin of `togglePlacement`. Kept separate rather than branching inside that one
       * because almost nothing is shared: a different evaluator, a different search for somewhere
       * to stand, a different cap, and a different set of things it is allowed to collide with.
       */
      toggleInteriorPlacement: (itemId) => {
        const originalVersion = get().data.property.version;
        let accepted = false;
        set((state) => {
          const item = getCatalogItem(itemId);
          const property = state.data.property;
          const existing = property.placements.find(
            (placement) => placement.itemId === itemId && placement.surface === "interior"
          );

          if (existing) {
            accepted = true;
            const remaining = property.placements.filter((placement) => placement !== existing);
            return {
              data: {
                ...state.data,
                property: {
                  ...property,
                  placements: settleInterior(remaining, true),
                  version: property.version + 1
                }
              },
              toast: show(`${item.name} moved to your collection`, "success")
            };
          }

          if (!property.homeType) {
            return { toast: show("Choose a home before you furnish the inside of it", "error") };
          }
          const here = indoorsIn(property.placements);
          const capacity = interiorCapacity(property.homeType);
          if (here.length >= capacity) {
            return { toast: show(`Your home already holds ${capacity} things inside`, "error") };
          }

          const scaleStep = PLACEMENT_SCALE_MAX_STEP;
          const position = defaultInteriorPosition({
            itemId, rotationQuarters: 0, scaleStep, homeType: property.homeType, taken: here
          });
          if (!position) {
            return { toast: show(`There is no clear floor for ${item.name}. Move something to make space.`, "error") };
          }

          accepted = true;
          const selectedVariants = Object.fromEntries(
            Object.entries(item.variants).map(([name, values]) => [name, values[0] ?? "standard"])
          );
          return {
            data: {
              ...state.data,
              property: {
                ...property,
                placements: [...property.placements, {
                  id: crypto.randomUUID(),
                  itemId,
                  surface: "interior" as const,
                  // Arrives on the floor. Standing it on a table is a drag, not a guess.
                  position: { x: position.x, y: 0, z: position.z },
                  rotationQuarters: 0,
                  scaleStep,
                  selectedVariants
                }],
                version: property.version + 1
              }
            },
            toast: show(`${item.name} placed`, "success")
          };
        });
        if (!accepted) return;
        const property = { ...get().data.property, version: originalVersion };
        void apiMutation("/api/v1/property", { property }, "PATCH").catch((error: unknown) => set({ toast: show(error instanceof Error ? error.message : "That placement could not be saved", "error") }));
      },
      togglePlacement: (itemId) => {
        const originalVersion = get().data.property.version;
        let accepted = false;
        set((state) => {
          const item = getCatalogItem(itemId);
          const property = state.data.property;
          // Matched on surface too, now that the same item id can stand in a yard or a room.
          const existing = property.placements.find(
            (placement) => placement.itemId === itemId && placement.surface === "exterior"
          );

          if (existing) {
            accepted = true;
            return {
              data: {
                ...state.data,
                property: {
                  ...property,
                  placements: property.placements.filter((placement) => placement !== existing),
                  version: property.version + 1
                }
              },
              toast: show(`${item.name} moved to your collection`, "success")
            };
          }

          // An item arrives at the size it was drawn, standing clear of the cottage. Where it
          // lands is a starting point — moving it is the member's job, not ours.
          const scaleStep = PLACEMENT_SCALE_MAX_STEP;
          const position = defaultPlacementPosition({
            itemId,
            rotationQuarters: 0,
            scaleStep,
            plotTier: property.plotTier,
            homeType: property.homeType,
            // Only what is actually in the yard: an indoor placement's coordinates are room-local,
            // so counting them here would make the middle of the garden look crowded.
            taken: property.placements.filter((placement) => placement.surface === "exterior")
          });
          if (!position) {
            // Three different problems wear the same "it did not go down" face, and the advice
            // for each is different: buy more ground, make it smaller, or shift what is already
            // out there. Say which.
            const largest = largestFittingScaleStep({ itemId, plotTier: property.plotTier, homeType: property.homeType });
            const message =
              largest === null
                ? `${item.name} needs more room than your yard has. Expand your parcel to fit it.`
                : largest < scaleStep
                  ? `${item.name} is too big for your yard at full size. Expand your parcel, or place it smaller.`
                  : `There is no open ground for ${item.name} right now. Move something to make space.`;
            return { toast: show(message, "error") };
          }

          accepted = true;
          const selectedVariants = Object.fromEntries(
            Object.entries(item.variants).map(([name, values]) => [name, values[0] ?? "standard"])
          );
          return {
            data: {
              ...state.data,
              property: {
                ...property,
                placements: [...property.placements, {
                  id: crypto.randomUUID(),
                  itemId,
                  surface: "exterior" as const,
                  position: { x: position.x, y: 0, z: position.z },
                  rotationQuarters: 0,
                  scaleStep,
                  selectedVariants
                }],
                version: property.version + 1
              }
            },
            toast: show(`${item.name} placed`, "success")
          };
        });
        if (!accepted) return;
        const property = { ...get().data.property, version: originalVersion };
        void apiMutation("/api/v1/property", { property }, "PATCH").catch((error: unknown) => set({ toast: show(error instanceof Error ? error.message : "That placement could not be saved", "error") }));
      },
      donateToProject: (copper) => {
        set((state) => {
        if (copper <= 0 || copper % 10 !== 0) return { toast: show("Donations use groups of 10 copper", "error") };
        if (state.data.wallet.copper < copper) return { toast: show("You do not have enough coins for that donation", "error") };
        return {
          data: { ...state.data, wallet: { copper: state.data.wallet.copper - copper }, project: addProjectEffort(state.data.project, copper / 10) },
          toast: show(`${copper / 10} effort added to the Town Square`, "success")
        };
        });
        void apiMutation("/api/v1/projects/donate", { copper }).catch((error: unknown) => set({ toast: show(error instanceof Error ? error.message : "That contribution could not be saved", "error") }));
      },
      sendGift: (itemId, recipientId) => {
        set((state) => {
        const catalogItem = getCatalogItem(itemId);
        if (!catalogItem.giftable) return { toast: show("That item cannot be gifted", "error") };
        if (state.data.wallet.copper < catalogItem.priceCopper) return { toast: show("You do not have enough coins for that gift", "error") };
        return {
          data: {
            ...state.data,
            wallet: { copper: state.data.wallet.copper - catalogItem.priceCopper },
            gifts: [...state.data.gifts, { id: crypto.randomUUID(), senderId: state.data.member.id, recipientId, itemId, status: "delivered", createdAt: new Date().toISOString() }]
          },
          toast: show(`${catalogItem.name} was sent`, "success")
        };
        });
        void apiMutation("/api/v1/gifts", { itemId, recipientId }).catch((error: unknown) => set({ toast: show(error instanceof Error ? error.message : "That gift could not be sent", "error") }));
      },
      saveQuestDraft: () => set((state) => {
        const result = validateQuestCopy(state.questDraft);
        if (!result.valid) return { toast: show(result.errors[0] ?? "Rewrite that quest", "error") };
        if (result.warnings.length) return { toast: show(result.warnings[0]!, "info") };
        void apiMutation("/api/v1/admin/quests", { prompt: state.questDraft, channelGroup: state.questChannelGroup, verification: state.questVerification })
          .catch((error: unknown) => set({ toast: show(error instanceof Error ? error.message : "That quest could not be saved", "error") }));
        return { toast: show("Quest saved for moderator review", "success") };
      }),
      showToast: (message, tone) => set({ toast: show(message, tone) }),
      receivePresence: (message) => set((state) => {
        if (!message || typeof message !== "object") return {};
        const value = message as { type?: string; memberId?: string; displayName?: string; x?: number; z?: number; reaction?: string; members?: Array<{ memberId?: string; displayName?: string }>; rewardCopper?: number; project?: ActivityBootstrap["project"] };
        if (value.type === "snapshot" && Array.isArray(value.members)) return { liveVisitors: value.members.filter((member) => member.memberId).map((member, index) => ({ memberId: member.memberId!, displayName: member.displayName ?? "Neighbor", x: Math.cos(index * 2.1) * 6, z: Math.sin(index * 2.1) * 6 })) };
        if (value.type === "project" && value.project) return { data: { ...state.data, project: value.project } };
        if (value.type === "reward" && value.project && value.memberId) {
          const ownReward = value.memberId === state.data.member.id;
          return {
            data: { ...state.data, wallet: ownReward ? { copper: state.data.wallet.copper + (value.rewardCopper ?? 0) } : state.data.wallet, project: value.project },
            ...(ownReward ? { toast: show(`Discord participation earned ${value.rewardCopper ?? 0} copper`, "success") } : {})
          };
        }
        if (!value.memberId) return {};
        const existing = state.liveVisitors.find((visitor) => visitor.memberId === value.memberId);
        if (value.type === "leave") return { liveVisitors: state.liveVisitors.map((visitor) => visitor.memberId === value.memberId ? { ...visitor, disconnectedAt: Date.now() } : visitor) };
        const visitor: LiveVisitor = {
          memberId: value.memberId,
          displayName: value.displayName ?? existing?.displayName ?? "Neighbor",
          x: typeof value.x === "number" ? value.x : existing?.x ?? 0,
          z: typeof value.z === "number" ? value.z : existing?.z ?? 0,
          ...(value.reaction ? { reaction: value.reaction } : {})
        };
        return { liveVisitors: [...state.liveVisitors.filter((candidate) => candidate.memberId !== visitor.memberId), visitor] };
      })
    }),
    {
      name: "tiny-tribe-village",
      // The persisted blob contains the generated world. Without this bump, a stale v1
      // layout would rehydrate straight over the new generator and none of the spacing
      // or terrain work would ever be visible locally.
      // v3 drops the old house colours and tier in favour of a single chosen home type.
      // v4 replaces socket-pinned placements with free-form position, rotation and size.
      version: 4,
      migrate: () => ({ data: createDemoBootstrap() }) as Partial<VillageStore>,
      partialize: (state) => ({
        data: state.data,
        quality: state.quality,
        reducedMotion: state.reducedMotion,
        ambienceVolume: state.ambienceVolume
      })
    }
  )
);

saveProperty = createPropertySaver(
  () => useVillageStore.getState().data,
  (message) => useVillageStore.setState({ toast: show(message, "error") })
);

// Dev-only handle, for driving state from the console when checking the scene.
if (import.meta.env.DEV) (window as unknown as { villageStore?: unknown }).villageStore = useVillageStore;
